import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_PANE_ID_PREFIX } from '../constants';
import type { ChartV2Series, ChartV2SeriesType } from '../types/chart-v2.types';
import {
  compactPaneIndices,
  InternalSeriesMeta,
  seriesPaneId,
} from '../utils/panes';

export interface UseSeriesMetaOptions {
  series: ChartV2Series[];
  onSeriesTypeChange?: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
  onSeriesMovePane?: (seriesId: string, direction: 'up' | 'down') => void;
  onPaneMove?: (paneId: string, direction: 'up' | 'down') => void;
}

export interface UseSeriesMetaResult {
  seriesMeta: Record<string, InternalSeriesMeta>;
  handleSeriesColorChange: (seriesId: string, color: string) => void;
  handlePerSeriesTypeChange: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
  handleSeriesMovePane: (seriesId: string, direction: 'up' | 'down') => void;
  handlePaneMove: (
    paneId: string,
    direction: 'up' | 'down',
    visibleSeries: ChartV2Series[]
  ) => void;
}

const parseIndexFromId = (id: string): number => {
  const n = Number(id.replace(DEFAULT_PANE_ID_PREFIX, ''));

  return Number.isFinite(n) ? n : 0;
};

export const useSeriesMeta = ({
  series,
  onSeriesTypeChange,
  onSeriesMovePane,
  onPaneMove,
}: UseSeriesMetaOptions): UseSeriesMetaResult => {
  const [seriesMeta, setSeriesMeta] = useState<
    Record<string, InternalSeriesMeta>
  >({});

  // Reconcile internal meta with the currently-provided series list. Keep
  // existing entries for known ids, initialise new ones, drop stale ones.
  useEffect(() => {
    setSeriesMeta(prev => {
      const next: Record<string, InternalSeriesMeta> = {};

      for (const s of series) {
        next[s.id] = prev[s.id] ?? { paneIndex: 0 };
      }

      return next;
    });
  }, [series]);

  const handleSeriesColorChange = useCallback(
    (seriesId: string, color: string) => {
      setSeriesMeta(prev => ({
        ...prev,
        [seriesId]: { ...(prev[seriesId] ?? { paneIndex: 0 }), color },
      }));
    },
    []
  );

  const handlePerSeriesTypeChange = useCallback(
    (seriesId: string, seriesType: ChartV2SeriesType) => {
      setSeriesMeta(prev => ({
        ...prev,
        [seriesId]: { ...(prev[seriesId] ?? { paneIndex: 0 }), seriesType },
      }));
      onSeriesTypeChange?.(seriesId, seriesType);
    },
    [onSeriesTypeChange]
  );

  const handleSeriesMovePane = useCallback(
    (seriesId: string, direction: 'up' | 'down') => {
      setSeriesMeta(prev => {
        const current = prev[seriesId];

        if (!current) {
          return prev;
        }

        const next =
          direction === 'up'
            ? Math.max(0, current.paneIndex - 1)
            : current.paneIndex + 1;

        if (next === current.paneIndex) {
          return prev;
        }

        const updated: Record<string, InternalSeriesMeta> = {
          ...prev,
          [seriesId]: { ...current, paneIndex: next },
        };

        return compactPaneIndices(updated);
      });
      onSeriesMovePane?.(seriesId, direction);
    },
    [onSeriesMovePane]
  );

  const handlePaneMove = useCallback(
    (
      paneId: string,
      direction: 'up' | 'down',
      visibleSeries: ChartV2Series[]
    ) => {
      setSeriesMeta(prev => {
        // Build the current pane order from the visible series so the move
        // operates on the same set the user actually sees.
        const paneOrder: string[] = [];
        const paneIdToOrder = new Map<string, number>();

        visibleSeries.forEach(s => {
          const id = seriesPaneId(s);

          if (!paneIdToOrder.has(id)) {
            paneIdToOrder.set(id, paneOrder.length);
            paneOrder.push(id);
          }
        });

        const currentOrderIndex = paneIdToOrder.get(paneId);

        if (currentOrderIndex === undefined) {
          return prev;
        }

        const targetOrderIndex =
          direction === 'up' ? currentOrderIndex - 1 : currentOrderIndex + 1;

        if (targetOrderIndex < 0 || targetOrderIndex >= paneOrder.length) {
          return prev;
        }

        const targetPaneId = paneOrder[targetOrderIndex];
        const fromIdx = parseIndexFromId(paneId);
        const toIdx = parseIndexFromId(targetPaneId);
        const next: Record<string, InternalSeriesMeta> = { ...prev };

        // Swap stored paneIndex values for every series in either pane.
        for (const id of Object.keys(next)) {
          const meta = next[id];

          if (meta.paneIndex === fromIdx) {
            next[id] = { ...meta, paneIndex: toIdx };
          } else if (meta.paneIndex === toIdx) {
            next[id] = { ...meta, paneIndex: fromIdx };
          }
        }

        return compactPaneIndices(next);
      });
      onPaneMove?.(paneId, direction);
    },
    [onPaneMove]
  );

  return {
    seriesMeta,
    handleSeriesColorChange,
    handlePerSeriesTypeChange,
    handleSeriesMovePane,
    handlePaneMove,
  };
};
